import { Station } from '../../types';

// Define the possible types of matrix values
export type MatrixValue = Station[] | number[][];

// Define the matrix definition structure
interface MatrixDefinition {
  view: string;
  displayName: string;
  allowInResults: boolean;
  hasCurrency: boolean;
  type: string;
  valueType: 'stations' | 'numbers';
  group: string;
}

// Define the matrix definitions as the single source of truth
export const MATRIX_DEFINITIONS = {
  cities: {
    view: 'cities',
    displayName: 'Cities',
    allowInResults: true,
    hasCurrency: false,
    type: 'cities' as const,
    valueType: 'stations' as const,
    group: 'railNetwork' as const
  },
  adjacency: {
    view: 'adjacency',
    displayName: 'Adjacency (km)',
    allowInResults: true,
    hasCurrency: false,
    type: 'adjacency' as const,
    valueType: 'numbers' as const,
    group: 'railNetwork' as const
  },
  distances: {
    view: 'distances',
    displayName: 'Distances (km)',
    allowInResults: true,
    hasCurrency: false,
    type: 'distances' as const,
    valueType: 'numbers' as const,
    group: 'railNetwork' as const
  },
  travelTime: {
    view: 'travel-time',
    displayName: 'Travel Time',
    allowInResults: true,
    hasCurrency: false,
    type: 'travelTime' as const,
    valueType: 'numbers' as const,
    group: 'railNetwork' as const
  },
  gravity: {
    view: 'gravity',
    displayName: 'Gravity',
    allowInResults: true,
    hasCurrency: false,
    type: 'gravity' as const,
    valueType: 'numbers' as const,
    group: 'railPassengers' as const
  },
  ridership: {
    view: 'ridership',
    displayName: 'Ridership',
    allowInResults: true,
    hasCurrency: false,
    type: 'ridership' as const,
    valueType: 'numbers' as const,
    group: 'railPassengers' as const
  },
  passengerKm: {
    view: 'passenger-km',
    displayName: 'Passenger-Kilometers',
    allowInResults: true,
    hasCurrency: false,
    type: 'passengerKm' as const,
    valueType: 'numbers' as const,
    group: 'railPassengers' as const
  },
  passengersBySegment: {
    view: 'passengers-by-segment',
    displayName: 'Passengers by Segment',
    allowInResults: true,
    hasCurrency: false,
    type: 'passengersBySegment' as const,
    valueType: 'numbers' as const,
    group: 'railPassengers' as const
  },
  passengersBySegmentKm: {
    view: 'passengers-by-segment-km',
    displayName: 'Passengers by Segment-km',
    allowInResults: true,
    hasCurrency: false,
    type: 'passengersBySegmentKm' as const,
    valueType: 'numbers' as const,
    group: 'railPassengers' as const
  },
  trainsPerDay: {
    view: 'trains-per-day',
    displayName: 'Trains per Day',
    allowInResults: true,
    hasCurrency: false,
    type: 'trainsPerDay' as const,
    valueType: 'numbers' as const,
    group: 'trains' as const
  },
  trainsPerSegment: {
    view: 'trains-per-segment',
    displayName: 'Trains per Segment',
    allowInResults: true,
    hasCurrency: false,
    type: 'trainsPerSegment' as const,
    valueType: 'numbers' as const,
    group: 'trains' as const
  },
  tracks: {
    view: 'tracks',
    displayName: 'Tracks',
    allowInResults: true,
    hasCurrency: false,
    type: 'tracks' as const,
    valueType: 'numbers' as const,
    group: 'trains' as const
  },
  revenue: {
    view: 'revenue',
    displayName: 'Revenue',
    allowInResults: true,
    hasCurrency: true,
    type: 'revenue' as const,
    valueType: 'numbers' as const,
    group: 'railEconomics' as const
  },
  fares: {
    view: 'ticket',
    displayName: 'Ticket Prices',
    allowInResults: false,
    hasCurrency: true,
    type: 'fares' as const,
    valueType: 'numbers' as const,
    group: 'railEconomics' as const
  },
  businessFares: {
    view: 'ticket-business',
    displayName: 'Ticket Price: Business',
    allowInResults: true,
    hasCurrency: true,
    type: 'businessFares' as const,
    valueType: 'numbers' as const,
    group: 'railEconomics' as const
  },
  economyFares: {
    view: 'ticket-economy',
    displayName: 'Ticket Price: Economy',
    allowInResults: true,
    hasCurrency: true,
    type: 'economyFares' as const,
    valueType: 'numbers' as const,
    group: 'railEconomics' as const
  },
  discountFares: {
    view: 'ticket-discount',
    displayName: 'Ticket Price: Discount',
    allowInResults: true,
    hasCurrency: true,
    type: 'discountFares' as const,
    valueType: 'numbers' as const,
    group: 'railEconomics' as const
  },
  constructionCost: {
    view: 'construction-cost',
    displayName: 'Construction Cost',
    allowInResults: true,
    hasCurrency: true,
    type: 'constructionCost' as const,
    valueType: 'numbers' as const,
    group: 'railEconomics' as const
  },
  constructionCostPerTicket: {
    view: 'construction-cost-ticket',
    displayName: 'Construction Cost per Ticket',
    allowInResults: true,
    hasCurrency: false,
    type: 'constructionCostPerTicket' as const,
    valueType: 'numbers' as const,
    group: 'railEconomics' as const
  },
  flightDistances: {
    view: 'flight-distances',
    displayName: 'Flight Distances (km)',
    allowInResults: true,
    hasCurrency: false,
    type: 'flightDistances' as const,
    valueType: 'numbers' as const,
    group: 'comparison' as const
  },
  flightTime: {
    view: 'flight-time',
    displayName: 'Flight Time (+1h airport wait)',
    allowInResults: true,
    hasCurrency: false,
    type: 'flightTime' as const,
    valueType: 'numbers' as const,
    group: 'comparison' as const
  },
  drivingDistances: {
    view: 'driving-distances',
    displayName: 'Driving Distances (km)',
    allowInResults: true,
    hasCurrency: false,
    type: 'drivingDistances' as const,
    valueType: 'numbers' as const,
    group: 'comparison' as const
  },
  drivingTime: {
    view: 'driving-time',
    displayName: 'Driving Time',
    allowInResults: true,
    hasCurrency: false,
    type: 'drivingTime' as const,
    valueType: 'numbers' as const,
    group: 'comparison' as const
  },
  trainEmissions: {
    view: 'train-emissions',
    displayName: 'Emissions: Train (CO2 kg)',
    allowInResults: true,
    hasCurrency: false,
    type: 'trainEmissions' as const,
    valueType: 'numbers' as const,
    group: 'emissions' as const
  },
  flyingEmissions: {
    view: 'flight-emissions',
    displayName: 'Emissions: Flying (CO2 kg)',
    allowInResults: true,
    hasCurrency: false,
    type: 'flyingEmissions' as const,
    valueType: 'numbers' as const,
    group: 'emissions' as const
  },
  drivingEmissions: {
    view: 'driving-emissions',
    displayName: 'Emissions: Driving (CO2 kg)',
    allowInResults: true,
    hasCurrency: false,
    type: 'drivingEmissions' as const,
    valueType: 'numbers' as const,
    group: 'emissions' as const
  }
} as const;

// Generate the MatrixType from the definitions
export type MatrixType = typeof MATRIX_DEFINITIONS[keyof typeof MATRIX_DEFINITIONS]['type'];

// Generate a type that maps matrix types to their value types
export type MatrixData = {
  [K in keyof typeof MATRIX_DEFINITIONS]: typeof MATRIX_DEFINITIONS[K]['valueType'] extends 'stations' 
    ? Station[]
    : number[][];
};

// Derive the matrix to view mapping
export const MATRIX_TO_VIEW: Record<MatrixType, string> = Object.entries(MATRIX_DEFINITIONS).reduce(
  (acc, [matrix, def]) => ({ ...acc, [matrix]: def.view }),
  {} as Record<MatrixType, string>
);

// Derive the view to matrix mapping
export const VIEW_TO_MATRIX: Record<string, MatrixType> = Object.entries(MATRIX_DEFINITIONS).reduce(
  (acc, [matrix, def]) => ({ ...acc, [def.view]: matrix as MatrixType }),
  {} as Record<string, MatrixType>
);

// Get all allowed views for the results page
export const ALLOWED_RESULT_VIEWS: string[] = Object.values(MATRIX_DEFINITIONS)
  .filter(def => def.allowInResults)
  .map(def => def.view);

// Get display names for matrices
export const MATRIX_DISPLAY_NAMES: Record<MatrixType, string> = Object.entries(MATRIX_DEFINITIONS).reduce(
  (acc, [matrix, def]) => ({ ...acc, [matrix]: def.displayName }),
  {} as Record<MatrixType, string>
);

// Convert a matrix type to a URL view
export function matrixToView(matrixType: MatrixType): string {
  return MATRIX_TO_VIEW[matrixType];
}

// Convert a URL view to a matrix type
export function viewToMatrix(view: string): MatrixType | undefined {
  return VIEW_TO_MATRIX[view];
}

// Get the default matrix type
export function getDefaultMatrixType(): MatrixType {
  return 'cities';
}

// Get the default view
export function getDefaultView(): string {
  return 'cities';
} 